import React, { useEffect, useState, useCallback } from 'react';
import { usePermissions } from '../../context/PermissionContext';
import { useStore } from '../../context/StoreContext';
import { dashboardAPI } from '../../api';
import { getWidgetsForRole } from '../../config/dashboardConfig';
import { renderWidget } from './widgets/index';
import DashboardKPIRow from './DashboardKPIRow';
import DashboardAlerts from './DashboardAlerts';
import DashboardQuickActions from './DashboardQuickActions';
import DashboardInsights from './DashboardInsights';
import DashboardHealthScore from './DashboardHealthScore';
import DashboardPriorityActions from './DashboardPriorityActions';
import OnboardingChecklist from '../onboarding/OnboardingChecklist';

/**
 * DynamicDashboard — single-fetch, decision-first SaaS dashboard.
 *
 * Visual hierarchy:
 *   1. KPI Row           — primary numbers (what happened)
 *   2. Priority Actions  — urgent situation-specific CTAs
 *   3. Quick Actions     — common recurring actions
 *   4. Alerts/Insights   — secondary context
 *   5. Widgets           — detail on demand
 */

// Store type → human-readable badge label
const STORE_TYPE_LABELS = {
  small_retail:  'Retail Store',
  supermarket:   'Supermarket',
  jewellery:     'Jewellery Store',
  bridal_rental: 'Bridal Rental',
};

// Store type → one-line value prop tagline
const TIME_OPTIONS = [
  { label: '7d',  value: 7  },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const DAYS_KEY = 'dashboard_days';

function timeSince(date) {
  if (!date) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr ago`;
}

const DynamicDashboard = () => {
  const { storeRole } = usePermissions();
  const { selectedStore } = useStore();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const [days, setDaysState] = useState(() => {
    const saved = localStorage.getItem(DAYS_KEY);
    return saved ? Number(saved) : 30;
  });

  const setDays = (val) => {
    setDaysState(val);
    localStorage.setItem(DAYS_KEY, String(val));
  };

  const storeId   = selectedStore?.id;
  const storeType = selectedStore?.store_type || 'small_retail';
  const currency  = selectedStore?.currency || '';
  const role      = storeRole || 'cashier';
  const widgets   = getWidgetsForRole(role, storeType);

  // New user = fewer than 3 products (progressive disclosure for quick actions)
  const isNewUser = !loading && (selectedStore?.products_count ?? 0) < 3;

  // Empty store = truly no data yet → show focused welcome/onboarding only
  const isEmptyStore = !loading
    && (selectedStore?.products_count ?? 0) === 0
    && (selectedStore?.sales_count ?? 0) === 0;

  const loadAll = useCallback(async (forceRefresh = false) => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getOverview(storeId, days, forceRefresh);
      setDashboardData(res.data);
      setLastFetched(new Date());
    } catch {
      setError('Could not load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [storeId, days]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const alerts       = dashboardData?.alerts   || [];
  const insights     = dashboardData?.insights || [];
  const healthScore  = dashboardData?.health_score  ?? null;
  const healthStatus = dashboardData?.health_status ?? 'good';
  const sharedProps  = { storeId, storeType, role, data: dashboardData, loading };

  const storeTypeLabel = STORE_TYPE_LABELS[storeType] || 'Dashboard';

  // ── Welcome state: empty store, no data yet ────────────────────────────────
  // Show a focused setup screen — no KPIs, no widgets, no noise.
  if (isEmptyStore) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-welcome">
          <div className="dashboard-welcome__text">
            <h1 className="dashboard-welcome__title">
              {getGreeting()}! Welcome to {selectedStore?.name || 'your store'}.
            </h1>
            <p className="dashboard-welcome__sub">
              You're all set. Complete the steps below and your dashboard will come alive with real data.
            </p>
          </div>
        </div>
        <OnboardingChecklist />
      </div>
    );
  }

  // ── Normal dashboard ────────────────────────────────────────────────────────
  return (
    <div className="dashboard-page">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="page-header page-header--dashboard">
        <div className="page-header__left">
          <div className="page-header__title-row">
            <h1 className="page-title">{getGreeting()}, {selectedStore?.name || 'your store'}</h1>
            <span className="store-type-badge">{storeTypeLabel}</span>
          </div>
          <p className="page-subtitle page-subtitle--date">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
            })}
          </p>
        </div>

        <div className="dashboard-header-actions">
          {/* Health score: renamed to "Store Status" for clarity */}
          {!loading && healthScore !== null && (
            <DashboardHealthScore score={healthScore} status={healthStatus} loading={false} />
          )}
          <div className="dashboard-time-filter">
            {TIME_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`time-filter-btn${days === opt.value ? ' time-filter-btn--active' : ''}`}
                onClick={() => setDays(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="dashboard-refresh-group">
            {lastFetched && !loading && (
              <span className="dashboard-updated">Updated {timeSince(lastFetched)}</span>
            )}
            <button
              className="btn btn-outline btn--sm"
              onClick={() => loadAll(true)}
              disabled={loading}
              title="Force refresh"
            >
              {loading ? '…' : '↻'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-banner--error">
          {error}{' '}
          <button className="btn-link" onClick={() => loadAll(true)}>Retry</button>
        </div>
      )}

      {/* ── Onboarding checklist — only while not fully set up ────────────── */}
      <OnboardingChecklist />

      {/* ── 1. KPI Row — the 4 numbers that matter ────────────────────────── */}
      <DashboardKPIRow data={dashboardData} loading={loading} currency={currency} days={days} />

      {/* ── 2. Priority Actions — urgent situation-specific CTAs ──────────── */}
      <DashboardPriorityActions data={dashboardData} loading={loading} />

      {/* ── 3. Quick Actions — common recurring actions ───────────────────── */}
      <DashboardQuickActions storeType={storeType} isNewUser={isNewUser} />

      {/* ── 4. Alerts + Insights — secondary context ─────────────────────── */}
      {(alerts.length > 0 || !loading) && (
        <div className="dashboard-two-col">
          <DashboardAlerts alerts={alerts} loading={loading} />
          <DashboardInsights insights={insights} loading={loading} />
        </div>
      )}

      {/* ── 5. Widgets — detail on demand, hidden for new users ───────────── */}
      {!isNewUser && widgets.length > 0 && (
        <div className="dashboard-section dashboard-section--widgets">
          <div className="dashboard-section__header">
            <h3 className="dashboard-section__title">Details</h3>
          </div>
          <div className="dashboard-widgets-grid">
            {widgets.map(widgetId => renderWidget(widgetId, sharedProps))}
          </div>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      {!loading && dashboardData && (
        <div className="dashboard-footer">
          <div className="dashboard-footer__trust">
            <span className="dashboard-footer__secure">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Data encrypted &amp; secure
            </span>
            <span className="dashboard-footer__dot">·</span>
            <span className="dashboard-footer__brand">Powered by Aiviora</span>
            <span className="dashboard-footer__dot">·</span>
            <button className="btn-link" onClick={() => loadAll(true)}>Refresh now</button>
          </div>
        </div>
      )}

    </div>
  );
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default DynamicDashboard;
